import {
  Box,
  Button,
  Center,
  ColorProps,
  ComponentWithAs,
  Flex,
  Icon,
  IconProps,
  Link,
  Spinner,
  StyleProps,
  ThemingProps,
  Tooltip,
} from "@chakra-ui/react";
import { IconType } from "react-icons";
import { NavigateFunction, NavLink, useNavigate } from "react-router-dom";
import { useGlossary } from "src/hooks/useGlossary";
import { useNavLinkState } from "src/hooks/useNavLinkState";
import { isNotNull } from "src/services/predicates";
import { TextWithOverflowTooltip } from "../TextWithOverflowTooltip";
import { LeftNavContextType, useLeftNav } from "./LeftNavContext";
import { Mode } from "./types";

export type NavItemIconVisibility = "all" | Mode;
export type NavItemIconType = IconType | ComponentWithAs<"svg", IconProps>;

export type NavItemElement = React.ReactElement<NavItemProps>;
export interface NavItemProps {
  url?: string;
  /**
   * Match these URL params when determining if the item is active. If omitted,
   * the item will only be considered active if the URL matches exactly.
   */
  matchUrlParams?: string[];
  label: string;
  tooltip?: string;
  /** Apply glossary to the label; defaults to true. */
  applyGlossary?: boolean;
  leadIcon?: NavItemIconType;
  trailIcon?: NavItemIconType;
  leadIconVisibility?: NavItemIconVisibility;
  trailIconVisibility?: NavItemIconVisibility;
  isExternal?: boolean;
  showSpinner?: boolean;
  onClick?: (
    context: LeftNavContextType & { navigate: NavigateFunction }
  ) => void;
  menu?: () => React.ReactNode;
  styleProps?: StyleProps & ThemingProps;
}
export function NavItem({
  url,
  matchUrlParams,
  label: originalLabel,
  tooltip: originalTooltip,
  leadIcon,
  trailIcon,
  leadIconVisibility = "all",
  trailIconVisibility = "all",
  isExternal,
  showSpinner,
  onClick,
  menu,
  styleProps,
  applyGlossary = true,
}: NavItemProps) {
  const navigate = useNavigate();
  const context = useLeftNav();
  const { mode } = context;
  const state = useNavLinkState(url, matchUrlParams);
  const { glossary } = useGlossary();
  const label = applyGlossary ? glossary`${originalLabel}` : originalLabel;
  const tooltip =
    applyGlossary && isNotNull(originalTooltip)
      ? glossary`${originalTooltip}`
      : originalTooltip ?? label;

  const itemProps = {
    padding: "2",
    width: "100%",
    minWidth: "2.25rem",
    "aria-current": state === "active",
    _hover: {}, // Prevent underlining on hover.
    ...styleProps,
  };
  let item;

  const childNode = children({
    mode,
    label,
    leadIcon,
    trailIcon,
    leadIconVisibility,
    trailIconVisibility,
    showSpinner,
    // Should this be a separate prop, e.g. `iconColor`?
    color: styleProps?.color,
  });

  if (onClick !== undefined) {
    item = (
      <Button
        variant="outline"
        onClick={() => onClick({ ...context, navigate })}
        display="flex"
        justifyContent="left"
        alignItems="center"
        {...itemProps}
      >
        {childNode}
      </Button>
    );
  } else if (url === undefined) {
    item = <Flex {...itemProps}>{childNode}</Flex>;
  } else if (isExternal) {
    item = (
      <Link href={url} isExternal {...itemProps}>
        {childNode}
      </Link>
    );
  } else {
    item = (
      <Link as={NavLink} to={url} {...itemProps}>
        {childNode}
      </Link>
    );
  }

  return (
    <Box
      display="flex"
      as="li"
      margin="0"
      padding="0"
      borderRadius="5"
      justifyContent="center"
      alignItems="center"
      alignSelf="stretch"
      {...(mode === "compact" ? { width: "2.25rem" } : { width: "100%" })}
      color={state === "active" ? "primary.500" : undefined}
      backgroundColor={state === "active" ? "primary.100" : "transparent"}
      _hover={{ backgroundColor: "primary.50" }}
      transition="background-color .25s"
    >
      <Tooltip
        isDisabled={mode !== "compact"}
        label={tooltip}
        placement="right"
        hasArrow
      >
        {item}
      </Tooltip>
      {mode === "full" && menu && (
        <Center w="5" h="5" margin="2">
          {menu()}
        </Center>
      )}
    </Box>
  );
}

interface ChildrenProps {
  mode: Mode;
  label: string;
  leadIcon?: NavItemIconType;
  trailIcon?: NavItemIconType;
  leadIconVisibility?: NavItemIconVisibility;
  trailIconVisibility?: NavItemIconVisibility;
  showSpinner?: boolean;
  color?: ColorProps["color"];
}

function children({
  mode,
  label,
  leadIcon,
  trailIcon,
  leadIconVisibility,
  trailIconVisibility,
  showSpinner,
  color = "primary.500",
}: ChildrenProps) {
  return (
    <Flex alignItems="center" gap="4">
      {showSpinner && <Spinner />}
      {leadIcon &&
        (mode === leadIconVisibility || leadIconVisibility === "all") && (
          <Icon w="5" h="5" as={leadIcon} color={color} aria-hidden="true" />
        )}
      {mode === "full" && leadIconVisibility === "compact" && (
        <Box width="5"></Box>
      )}
      {mode === "full" && (
        <TextWithOverflowTooltip
          fontWeight="medium"
          fontSize="sm"
          wordBreak="break-word"
          tooltipPlacement="right"
          hasArrow
          content={label}
        />
      )}
      {(mode === trailIconVisibility || trailIconVisibility === "all") &&
        trailIcon && (
          <Icon w="5" h="5" as={trailIcon} color={color} aria-hidden="true" />
        )}
    </Flex>
  );
}
